import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface FAQCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category_id: string | null;
  display_order: number;
  is_published: boolean;
  view_count: number;
  helpful_count: number;
  not_helpful_count: number;
  keywords: string[];
  related_links: Record<string, any>;
  video_url: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  faq_categories?: FAQCategory;
}

export const useFAQCategories = () => {
  return useQuery({
    queryKey: ["faq-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faq_categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order");

      if (error) throw error;
      return data as FAQCategory[];
    },
  });
};

export const useFAQs = (categorySlug?: string, searchTerm?: string) => {
  return useQuery({
    queryKey: ["faqs", categorySlug, searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("faq_items")
        .select("*, faq_categories(*)")
        .eq("is_published", true)
        .order("display_order");

      if (categorySlug) {
        const { data: category } = await supabase
          .from("faq_categories")
          .select("id")
          .eq("slug", categorySlug)
          .single();

        if (category) {
          query = query.eq("category_id", category.id);
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      let faqs = data as FAQItem[];

      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        faqs = faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(term) ||
            faq.answer.toLowerCase().includes(term) ||
            faq.keywords.some((keyword) => keyword.toLowerCase().includes(term))
        );
      }

      return faqs;
    },
  });
};

export const useAllFAQs = () => {
  return useQuery({
    queryKey: ["all-faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faq_items")
        .select("*, faq_categories(*)")
        .order("display_order");

      if (error) throw error;
      return data as FAQItem[];
    },
  });
};

export const useTrackFAQView = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (faqId: string) => {
      const { data: faq } = await supabase
        .from("faq_items")
        .select("view_count")
        .eq("id", faqId)
        .single();

      if (!faq) throw new Error("FAQ not found");

      const { error } = await supabase
        .from("faq_items")
        .update({ view_count: faq.view_count + 1 })
        .eq("id", faqId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      queryClient.invalidateQueries({ queryKey: ["all-faqs"] });
    },
  });
};

export const useTrackHelpful = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      faqId,
      isHelpful,
    }: {
      faqId: string;
      isHelpful: boolean;
    }) => {
      const { data: faq } = await supabase
        .from("faq_items")
        .select("helpful_count, not_helpful_count")
        .eq("id", faqId)
        .single();

      if (!faq) throw new Error("FAQ not found");

      const updates = isHelpful
        ? { helpful_count: faq.helpful_count + 1 }
        : { not_helpful_count: faq.not_helpful_count + 1 };

      const { error } = await supabase
        .from("faq_items")
        .update(updates)
        .eq("id", faqId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      queryClient.invalidateQueries({ queryKey: ["all-faqs"] });
    },
  });
};

export const useCreateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (faq: Partial<FAQItem>) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from("faq_items")
        .insert([{
          question: faq.question || "",
          answer: faq.answer || "",
          category_id: faq.category_id,
          display_order: faq.display_order || 0,
          is_published: faq.is_published || false,
          keywords: faq.keywords || [],
          related_links: faq.related_links || {},
          video_url: faq.video_url || null,
          created_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      queryClient.invalidateQueries({ queryKey: ["all-faqs"] });
      toast({
        title: "Success",
        description: "FAQ created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create FAQ",
        variant: "destructive",
      });
      console.error(error);
    },
  });
};

export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FAQItem> & { id: string }) => {
      const { data, error } = await supabase
        .from("faq_items")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      queryClient.invalidateQueries({ queryKey: ["all-faqs"] });
      toast({
        title: "Success",
        description: "FAQ updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update FAQ",
        variant: "destructive",
      });
      console.error(error);
    },
  });
};

export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("faq_items")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faqs"] });
      queryClient.invalidateQueries({ queryKey: ["all-faqs"] });
      toast({
        title: "Success",
        description: "FAQ deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete FAQ",
        variant: "destructive",
      });
      console.error(error);
    },
  });
};
